import { Box, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import Loading from "../Loading";
import ExternalForm, { type IFormValues } from "./ExternalForm";
import {
    type ExternalEditMutation,
    type ExternalEditMutationVariables,
    type ExternalQuery,
    type ExternalQueryVariables,
    editMutation,
    externalQuery,
} from "./queries";

function Edit(): JSX.Element {
    const { externalId } = useParams();
    const navigate = useNavigate();
    const [editResult, executeEdit] = useMutation<ExternalEditMutation, ExternalEditMutationVariables>(editMutation);
    const [result] = useQuery<ExternalQuery, ExternalQueryVariables>({
        query: externalQuery,
        variables: {
            externalId: Number.parseInt(externalId!, 10),
        },
    });

    const handleSubmit = async (values: IFormValues) => {
        const result = await executeEdit({
            data: {
                id: Number.parseInt(externalId!, 10),
                name: values.name,
                allowDocumentIntelligence: values.allowDocumentIntelligence,
                allowEmbeddings: values.allowEmbeddings,
                allowTranscribe: values.allowTranscribe,
                aiPersonalities: values.aiPersonalities,
            },
        });
        if (result.error) {
            notifications.show({
                title: "Failed to save External access rights",
                message: result.error.message,
            });
        } else {
            const external = result.data!.externalEdit;
            notifications.show({
                title: "External saved",
                message: `External access right ${external.name} updated`,
            });
            navigate("..");
        }
    };

    let main = null;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else {
        const external = result.data?.external;
        if (!external) {
            main = <Box>External not found</Box>;
        } else {
            const initialValues = {
                name: external.name,
                allowDocumentIntelligence: external.allowDocumentIntelligence,
                allowEmbeddings: external.allowEmbeddings,
                allowTranscribe: external.allowTranscribe,
                aiPersonalities:
                    external.externalPersonalities.edges?.map((edge) => edge!.node!.aiPersonality.publicId) || [],
            };
            main = (
                <ExternalForm
                    handleSubmit={handleSubmit}
                    initialValues={initialValues}
                    fetching={editResult.fetching}
                />
            );
        }
    }

    return (
        <>
            <Title order={2}>Edit external</Title>
            {main}
            <Link to="..">Back</Link>
        </>
    );
}

export default Edit;
